import React, { useEffect, useMemo, useState } from 'react';
import CellaPermesso from './CellaPermesso';
import { livelloMassimo } from '../../../hooks/useMe';

/** Undici colonne col nome dell'enum non ci stanno: il nome pieno e' nel titolo dell'intestazione. */
const ETICHETTE_RISORSE = {
  UTENTE: 'Utenti',
  GRUPPO_UTENTI: 'Gruppi ut.',
  GRUPPO_TORNEI: 'Circuiti',
  EDIZIONE: 'Edizioni',
  ISCRIZIONI: 'Iscrizioni',
  FORMULA: 'Formula',
  PLANNING: 'Planning',
  RISULTATI: 'Risultati',
  STATISTICHE: 'Statist.',
  FOTO: 'Foto',
  ANAGRAFICA: 'Anagrafica'
};

/** La chiave di una cella nella mappa delle modifiche: ambito e risorsa insieme. */
export const chiaveCella = (ambito, risorsa) => `${ambito.tipo}:${ambito.id ?? ''}|${risorsa}`;

/**
 * La matrice di un principal su tutti gli ambiti insieme: le risorse in colonna, gli ambiti in
 * riga. Il globale in cima, poi i gruppi tornei in ordine alfabetico, e sotto ognuno, indentate,
 * le sue edizioni.
 *
 * Un gruppo che l'utente non amministra puo' comparire lo stesso, come intestazione di edizioni che
 * invece amministra: in quel caso il server non manda la sua matrice e le celle restano vuote.
 */
const GrigliaPermessi = ({ ambiti, matrici, celleEsplicite, me, modifiche, puoScrivereSu, onScegli }) => {
  const risorse = ambiti?.risorse ?? [];
  const livelli = ambiti?.livelli ?? [];

  const gruppi = useMemo(() => {
    const perId = new Map();

    (ambiti?.gruppiTornei ?? []).forEach(g => perId.set(g.id, { id: g.id, nome: g.nome, edizioni: [] }));

    (ambiti?.edizioni ?? []).forEach(ed => {
      if (!perId.has(ed.gruppoTornei))
        perId.set(ed.gruppoTornei, { id: ed.gruppoTornei, nome: `circuito ${ed.gruppoTornei}`, edizioni: [] });
      perId.get(ed.gruppoTornei).edizioni.push(ed);
    });

    return [...perId.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
  }, [ambiti]);

  const [aperti, setAperti] = useState(() => new Set());

  // I gruppi dove c'e' gia' qualcosa di scritto si aprono da soli: sono quelli che si va a
  // guardare. Cambiare principal ricalcola l'insieme, perche' celleEsplicite e' un array nuovo.
  useEffect(() => {
    const conCelle = new Set();

    (celleEsplicite ?? []).forEach(c => {
      if (c.scopeTipo === 'GT') conCelle.add(c.scopeId);
      if (c.scopeTipo === 'ED') {
        const edizione = (ambiti?.edizioni ?? []).find(e => e.id === c.scopeId);
        if (edizione) conCelle.add(edizione.gruppoTornei);
      }
    });

    setAperti(conCelle);
  }, [celleEsplicite, ambiti]);

  const apriChiudi = (id) => setAperti(prima => {
    const dopo = new Set(prima);
    if (!dopo.delete(id)) dopo.add(id);
    return dopo;
  });

  const matriceDi = (ambito) => (matrici ?? []).find(
    m => m.scopeTipo === ambito.tipo && (m.scopeId ?? null) === (ambito.id ?? null)) ?? null;

  const celleDi = (ambito) => {
    const matrice = matriceDi(ambito);
    const scrivibile = matrice !== null && puoScrivereSu(ambito);

    return risorse.map(risorsa => (
      <td key={risorsa}>
        {matrice === null
          ? <span className="permessi-vuota">-</span>
          : <CellaPermesso
              risorsa={risorsa}
              effettivo={matrice.celle?.[risorsa]}
              scelto={modifiche[chiaveCella(ambito, risorsa)]}
              livelli={livelli}
              massimo={livelloMassimo(me, ambito, risorsa)}
              bypassAttore={!!me?.bypass}
              modificabile={scrivibile}
              onScegli={valore => onScegli(ambito, risorsa, valore)}
            />}
      </td>
    ));
  };

  const riga = (ambito, nome, livelloRiga) => (
    <tr key={`${ambito.tipo}:${ambito.id ?? ''}`} className={`permessi-riga-${livelloRiga}`}>
      <th scope="row" className="permessi-ambito">{nome}</th>
      {celleDi(ambito)}
    </tr>
  );

  return (
    <div className="utenti-tabella-wrap permessi-wrap">
      <table className="utenti-tabella permessi-griglia">
        <thead>
          <tr>
            <th className="permessi-ambito">Ambito</th>
            {risorse.map(r => <th key={r} title={r}>{ETICHETTE_RISORSE[r] ?? r}</th>)}
          </tr>
        </thead>
        <tbody>
          {ambiti?.globale && riga({ tipo: 'GLB', id: null }, 'Globale', 0)}

          {gruppi.map(g => (
            <React.Fragment key={g.id}>
              <tr className="permessi-riga-1">
                <th scope="row" className="permessi-ambito">
                  {g.edizioni.length > 0
                    ? (
                      <button type="button" className="permessi-freccia"
                              aria-expanded={aperti.has(g.id)}
                              aria-label={`${aperti.has(g.id) ? 'Chiudi' : 'Apri'} ${g.nome}`}
                              onClick={() => apriChiudi(g.id)}>
                        <i className={`fas ${aperti.has(g.id) ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                      </button>
                    )
                    : <span className="permessi-freccia-vuota"></span>}
                  {g.nome}
                  {g.edizioni.length > 0 && <span className="utenti-nota"> ({g.edizioni.length})</span>}
                </th>
                {celleDi({ tipo: 'GT', id: g.id })}
              </tr>

              {aperti.has(g.id) && g.edizioni.map(ed => riga({ tipo: 'ED', id: ed.id }, ed.nome, 2))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GrigliaPermessi;
